"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useIntersection = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
var react_1 = __importDefault(require("react"));
var dom_1 = require("../functions/dom");
/**
 * Hook that uses `IntersectionObserver` to trigger `onIntersect` callback when element is in viewport.
 * Callback is called only once by default (can be changed using the `once` parameter).
 * @returns a callback ref that needs to be set on the element you want to observe.
 * @private
 * @example
 * const onIntersection = React.useCallback(() => {
 *   console.log('Element is in viewport!');
 * }, []);
 * const ref = useIntersection(onIntersection);
 * return (<div ref={ref}>One of many elements</div>);
 */
var useIntersection = function (onIntersect, options, once) {
    if (options === void 0) { options = {}; }
    if (once === void 0) { once = true; }
    var root = options.root, rootMargin = options.rootMargin, threshold = options.threshold;
    var observer = react_1.default.useRef();
    var setRef = react_1.default.useCallback(function (node) {
        var _a;
        if (!((_a = (0, dom_1.getWindow)()) === null || _a === void 0 ? void 0 : _a.IntersectionObserver)) {
            return;
        }
        if (observer.current) {
            observer.current.disconnect();
        }
        if (!node) {
            return;
        }
        observer.current = new IntersectionObserver(function (_a, obs) {
            var entry = _a[0];
            if (entry.isIntersecting) {
                if (once) {
                    obs.disconnect();
                }
                onIntersect();
            }
        }, { root: root, rootMargin: rootMargin, threshold: threshold });
        observer.current.observe(node);
    }, [onIntersect, once, root, rootMargin, threshold]);
    react_1.default.useEffect(function () { return function () { var _a; return (_a = observer.current) === null || _a === void 0 ? void 0 : _a.disconnect(); }; }, []);
    return setRef;
};
exports.useIntersection = useIntersection;
